import { Location, NgClass } from "@angular/common";
import {
    ChangeDetectionStrategy,
    Component,
    NgZone,
    OnDestroy,
    OnInit,
    inject
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { BusySpinnerComponent } from "@components/busy/busy-spinner/busy-spinner.component";
import { BlockComponent } from "@components/container-block/block.component";
import { HeaderComponent } from "@components/header/header.component";
import { IconComponent } from "@components/icon/icon.component";
import { InputComponent } from "@components/input/input.component";
import { PageComponent } from "@components/page/page.component";
import { RadiobuttonGroupComponent } from "@components/radiobuttongroup/radiobuttongroup.component";
import { SaveButtonComponent } from "@components/save-button/save-button.component";
import { SearchComponent } from "@components/search/search.component";
import { SimpleSelectorComponent } from "@components/simple-selector/simple-selector.component";
import { TextmultilineComponent } from "@components/textmultiline/textmultiline.component";
import { TranslatePipe } from "@ngx-translate/core";
import { Observable, Subject, combineLatest, firstValueFrom } from "rxjs";
import {
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    shareReplay,
    take,
    takeUntil,
    tap
} from "rxjs/operators";
import { BusyService } from "~app/common/components/busy/busy.service";
import { IMapGeometry } from "~app/common/components/map/map.settings";
import { PermittedDirective } from "~app/common/directives/permitted/permitted.directive";
import { CanBeDirty } from "~app/common/guards/dirty.guard";
import { RaskRoller } from "~app/common/models/constants";
import { ansvarigVirkeskoparePermission } from "~app/common/permissions/ansvarigVirkeskopare.permissions";
import { CommandManager } from "~app/common/services/cqs/commandManager";
import { RuleErrorContext, RuleSeverityLevel } from "~app/common/services/cqs/models/rule-validation-message";
import { DirtyFormService } from "~app/common/services/dirty-form.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { ANSKAFFNINGSFORMERSOKNING } from "~app/features/virkeskop/models/kontrakt-detail-model";
import { VirkesleverantorService } from "~app/features/virkeskop/virkesleverantor/services/virkesleverantor.service";
import { canEdit, updatePermissions } from "~app/role-permissions";
import * as VirkeskopFieldLengthConstants from "../constants/virkeskop-field-length-constants";
import { BUSY_CHANNEL_NAME } from "../constants/virkeskopngx-constants";
import { KontraktListComponent } from "../kontrakt/kontrakt-list/kontrakt-list.component";
import { KontraktVacsComponent } from "../kontrakt/kontrakt-vacs/kontrakt-vacs.component";
import { LeverantorerComponent } from "../leverantorer/leverantorer.component";
import { AnsvarigAnvandare, Avtal } from "../models/avtal-models";
import { VirkeskopKontraktstyp } from "../models/kontrakt-models";
import { AvtalMediatorService } from "../services/avtal-mediator.service";
import { KontaktpersonMediatorService } from "../services/kontaktperson-mediator.service";
import { KundMediatorService } from "../services/kund-mediator.service";
import { LeverantorMediatorService } from "../services/leverantor-mediator.service";
import { NavigationService } from "../services/navigation.service";
import { VirkeskopValidationService } from "../services/validation/virkeskop-validation.service";
import { VirkeskopValidationRuleDirective } from "../validation/directives/virkeskop-validation-rule.directive";
import { VirkeskopValidationIconPopoverComponent } from "../validation/validation-icon-popover/virkeskop-validation-icon-popover.component";
import { VirkeskopValidationMessageComponent } from "../validation/validation-message/virkeskop-validation-message.component";

@Component({
    selector: "rask-virkeskop-avtal",
    templateUrl: "./virkeskop-avtal.component.html",
    styleUrls: ["./virkeskop-avtal.component.scss"],
    providers: [
        LeverantorMediatorService,
        AvtalMediatorService,
        VirkeskopValidationService,
        CommandManager,
        NavigationService,
        KundMediatorService,
        KontaktpersonMediatorService,
        updatePermissions({
            role: RaskRoller.role.Virkeskopa,
            canView: ansvarigVirkeskoparePermission,
            canEdit: ansvarigVirkeskoparePermission
        })
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        PageComponent,
        BlockComponent,
        HeaderComponent,
        VirkeskopValidationMessageComponent,
        InputComponent,
        FormsModule,
        SearchComponent,
        VirkeskopValidationRuleDirective,
        SimpleSelectorComponent,
        TextmultilineComponent,
        RadiobuttonGroupComponent,
        LeverantorerComponent,
        KontraktListComponent,
        KontraktVacsComponent,
        SaveButtonComponent,
        BusySpinnerComponent,
        IconComponent,
        NgClass,
        VirkeskopValidationIconPopoverComponent,
        TranslatePipe,
        PermittedDirective
    ]
})
export class VirkeskopAvtalComponent implements OnInit, CanBeDirty, OnDestroy {
    protected anskaffingsformer = ANSKAFFNINGSFORMERSOKNING;
    protected ansvarigTitle = "Ansvarig virkesköpare";
    protected avtal: Avtal = null;
    protected avtalState = "Skapa";
    protected hasLeverantor: boolean;
    protected isSkogsvardFPV = false;
    protected isWorking = false;
    protected isWorking$: Observable<boolean>;
    protected saving = false;
    protected virkeskopAvtalId?: number;
    protected virkeskopKontraktId?: number;
    protected avtalBenamningMaxLength = VirkeskopFieldLengthConstants.AvtalBenamningMaxLength;
    protected canEdit = canEdit();

    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly avtalMediatorService = inject(AvtalMediatorService);
    private readonly busyService = inject(BusyService);
    private readonly destroy$ = new Subject<void>();
    private readonly dirtyFormService = inject(DirtyFormService);
    private readonly kontaktpersonMediatoService = inject(KontaktpersonMediatorService);
    private readonly leverantorMediatorService = inject(LeverantorMediatorService);
    private readonly location = inject(Location);
    private readonly navigationService = inject(NavigationService);
    private readonly ngZone = inject(NgZone);
    private readonly router = inject(Router);
    private readonly virkeskopValidationService = inject(VirkeskopValidationService);
    private readonly virkesleverantorService = inject(VirkesleverantorService);

    private hasErrors: boolean;
    private isDirty: boolean;

    protected get errorContext() {
        return RuleErrorContext.save;
    }

    protected get isNew() {
        return !this.virkeskopAvtalId;
    }

    isDirtyFn = async () => {
        if (!this.isDirty) {
            return true;
        }

        //Här måste specialfall hanteras då kontraktet ej är sparat och leverantör saknas
        if (this.avtal.id === undefined && !this.hasLeverantor) {
            return true;
        }

        const result = await this.dirtyFormService.dirtyFormModalAsync(true);
        switch (result) {
            case "ok":
                return true;
            case "save":
                this.avtalMediatorService.validate();
                // Vänta på att allt har laddat klart innan försök till att spara utförs
                await firstValueFrom(this.isWorking$.pipe(
                    filter(isWorking => isWorking === false),
                    take(1)
                ));

                if (this.canSave()) {
                    this.save();
                    return true;
                }
                return false;
            default:
                return false;
        }
    };

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngOnInit(): void {
        const navigationState = (this.location.getState() ?? {}) as {
            mapGoto?: IMapGeometry;
            [key: string]: unknown;
        };
        const { mapGoto, ...stateWithoutMapGoto } = navigationState;
        if (mapGoto) {
            this.location.replaceState(this.location.path(true), "", stateWithoutMapGoto);
            this.avtalMediatorService.mapGoto(mapGoto);
        }
        this.initSubscriptions();

        this.leverantorMediatorService.leverantorer$.pipe(
            takeUntil(this.destroy$)
        ).subscribe(x => {
            if (x.length > 0 && ObjectUtils.isNullOrUndefined(this.avtal.anskaffningsform)) {
                this.avtal.anskaffningsform = x[0].anskaffningsform;
                this.avtalMediatorService.updateAvtal(this.avtal);
            }
        });

        const avtalId$ = this.activatedRoute.params.pipe(
            map(params => params.id),
            tap(avtalId => {
                this.avtalMediatorService.resetState();
                this.virkeskopAvtalId = this.parseId(avtalId);
                this.initAvtal();
            })
        );

        this.activatedRoute.data.subscribe(data => {
            this.isSkogsvardFPV = data.isSkogsvardFPV ?? false;
            if (this.isSkogsvardFPV) {
                this.ansvarigTitle = "Kundansvarig uppdrag";
            }
        });

        const queryParams$ = this.activatedRoute.queryParams.pipe(
            map(params => ({
                kontraktId: params.kontraktId,
                atgardId: params.atgardId
            })));

        combineLatest([
            avtalId$,
            queryParams$
        ]).pipe(
            takeUntil(this.destroy$)
        ).subscribe(([
            avtalId,
            queryParams
        ]) => {
            const { kontraktId, atgardId } = queryParams;
            this.virkeskopKontraktId = this.parseId(kontraktId);
            this.navigationService.navigate(avtalId, kontraktId, atgardId);
        });
    }

    protected ansvarigAnvandareCompareWith(item: AnsvarigAnvandare, selected: AnsvarigAnvandare) {
        return item.userName === selected.userName;
    }

    protected ansvarigSaljareChanged($event) {
        this.avtal.ansvarigSaljare = $event;
        this.avtalEdited();
    }

    protected ansvarigVirkeskopareChanged($event) {
        this.avtal.ansvarigVirkeskopare = $event;
        this.avtalEdited();
    }

    protected avtalEdited() {
        this.avtalMediatorService.updateAvtal(this.avtal);
    }

    protected canSave(): boolean {
        return this.saving === false && this.isDirty && this.hasLeverantor && !this.hasErrors && !this.isWorking;
    }

    protected save(): void {
        if (!this.canSave()) {
            return;
        }
        this.saving = true;
        this.busyService.addWork(this.avtalMediatorService.save()).pipe(
            take(1)
        ).subscribe({
            // när validera + spara + ladda är klart...
            next: () => this.saving = false,
            // något gick fel i validering, sparning eller laddning
            error: () => this.saving = false
        });
    }

    protected searchForAnsvarigVirkeskopareTypeAhead = (input: string) => this.virkesleverantorService.getVirkesKopareTypeAhead(input)
        .pipe(map(typeahead =>
            typeahead.map(x => ({
                userName: x.kod,
                name: x.namn
            }))
        ));

    private avtalChanged(avtal?: Avtal) {
        if (this.isNew && avtal?.id) { // första sparning
            const mapGoto = this.avtalMediatorService.mapGoto$.value;
            this.router.navigate([avtal.id], {
                relativeTo: this.activatedRoute.parent,
                state: { mapGoto }
            });
            return;
        }

        this.avtal = avtal;
    }

    private getDefaultKontraktTyp(): string {
        if (this.isSkogsvardFPV) {
            return VirkeskopKontraktstyp.SkogsvardFPV;
        } else {
            return VirkeskopKontraktstyp.Rundvirke;
        }
    }

    private initAvtal() {
        if (this.isNew) {
            this.avtalMediatorService.newAvtal(this.getDefaultKontraktTyp());
        } else {
            this.avtalMediatorService.setAvtal(this.virkeskopAvtalId);
        }

        this.initAvtalHeading();
    }

    private initAvtalHeading(): void {
        this.avtalState = this.isNew ? "Skapa" : "Redigera";
    }

    private initSubscriptions(): void {
        const { avtal$, dirty$ } = this.avtalMediatorService;

        avtal$.subscribe(co => {
            const avtal = co?.current;
            this.avtalChanged(avtal);
        });

        dirty$.subscribe(dirty => {
            this.isDirty = dirty;
        });

        this.kontaktpersonMediatoService.hasKontakt$.pipe(
            takeUntil(this.destroy$)
        ).subscribe(hasKontakt => this.hasLeverantor = hasKontakt);

        this.virkeskopValidationService.visibleMessages$.subscribe(messages => {
            const errorMessages = messages.filter(x =>
                x.severity === RuleSeverityLevel.error
                && x.context === RuleErrorContext.save);
            this.hasErrors = errorMessages.length > 0;
        });

        this.isWorking$ = this.busyService.getChannel(BUSY_CHANNEL_NAME).isWorking.pipe(
            distinctUntilChanged(),
            debounceTime(200),
            shareReplay(1)
        );

        this.isWorking$.subscribe(isWorking => {
            this.ngZone.run(() => {
                this.isWorking = isWorking;
            });
        });
    }

    private parseId(parameter: any): number | undefined {
        if (parameter === "" || isNaN(parameter)) {
            return undefined;
        }

        const id = parseInt(parameter, 10);
        return id > 0 ? id : undefined;
    }
}
